import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import ColumnSearch from "../../../components/ColumnSearch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../../../stores/useDialogStore";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import { toast } from "react-toastify";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";

export default function UsersList() {
  const [status, setStatus] = useState("Approved");
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();

  // Selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const editCellMutation = useMutation({
    mutationFn: ({ userId, field, value }) =>
      axiosInstance.patch(`${window.REACT_APP_API_ENDPOINT}/users/${userId}`, {
        [field]: value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast.success("User updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update user. Please try again.");
      console.error("Update user error:", error);
    },
  });

  const roleOptions = [
    { id: "Admin", name: "Admin" },
    { id: "Technician", name: "Technician" },
    { id: "Stock Manager", name: "Stock Manager" },
  ];

  const { data: users, isLoading } = useQuery({
    queryKey: ["users", status, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      params.set("status", status);

      const response = await axiosInstance.get(
        `${
          window.REACT_APP_API_ENDPOINT
        }/trash/deleted-users?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  // Restore user mutation
  const restoreUserMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.post(`${window.REACT_APP_API_ENDPOINT}/trash/restore`, {
        id,
        type: "users",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      closeDialog();
      toast.success("User restored successfully!");
    },
    onError: (error) => {
      toast.error("Failed to restore user. Please try again.");
      console.error("Restore user error:", error);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/trash/delete-permanently`,
        {
          id,
          type: "users",
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      closeDialog();
      toast.success("User deleted permanently!");
    },
    onError: (error) => {
      toast.error("Failed to delete user. Please try again.");
      console.error("Delete user error:", error);
    },
  });

  const handleRestore = (id, userName) => {
    openDialog({
      title: "Restore User",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to restore{" "}
            <span className="font-bold">{userName}</span>?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                restoreUserMutation.mutate(id);
              }}
            >
              Restore
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleDelete = (id, userName) => {
    openDialog({
      title: "Delete User Permanently",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg text-red-600 font-semibold">
            Are you sure you want to permanently delete user "{userName}"?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                deleteUserMutation.mutate(id);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      await axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/users/${id}/status?status=${status}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      console.error("Status update error:", error);
    },
  });

  const handleStatusChange = (id, newStatus) => {
    statusMutation.mutate({ id, status: newStatus });
  };

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    params.set("status", status);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    params.set("status", status);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleClearFilters = () => {
    navigate(`${location.pathname}?status=${status}`);
  };

  // Selection handlers
  const usersList = users?.data?.data || [];
  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
    setSelectAll(newSelected.size === usersList.length);
  };
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers(new Set());
      setSelectAll(false);
    } else {
      setSelectedUsers(new Set(usersList.map((user) => user.id)));
      setSelectAll(true);
    }
  };
  const handleClearSelection = () => {
    setSelectedUsers(new Set());
    setSelectAll(false);
  };
  // Bulk actions
  const handleBulkRestore = () => {
    if (selectedUsers.size === 0) {
      toast.warning("Please select at least one user to restore.");
      return;
    }
    openDialog({
      title: "Restore Multiple Users",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to restore {selectedUsers.size} user
            {selectedUsers.size > 1 ? "s" : ""}?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              icon={ArrowPathIcon}
              onClick={() => {
                // Bulk restore
                axiosInstance
                  .post(`${window.REACT_APP_API_ENDPOINT}/trash/restore`, {
                    ids: Array.from(selectedUsers),
                    type: "users",
                  })
                  .then(() => {
                    queryClient.invalidateQueries(["users"]);
                    setSelectedUsers(new Set());
                    setSelectAll(false);
                    closeDialog();
                    toast.success("Users restored successfully!");
                  })
                  .catch(() => {
                    toast.error(
                      "Failed to restore some users. Please try again."
                    );
                  });
              }}
            >
              Restore All
            </Button>
          </div>
        </div>
      ),
    });
  };
  const handleBulkDelete = () => {
    if (selectedUsers.size === 0) {
      toast.warning("Please select at least one user to delete permanently.");
      return;
    }
    openDialog({
      title: "Delete Permanently",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg text-red-600 font-semibold">
            ⚠️ WARNING: This action cannot be undone!
          </p>
          <p className="text-lg">
            Are you sure you want to permanently delete {selectedUsers.size}{" "}
            user{selectedUsers.size > 1 ? "s" : ""}? This action will completely
            remove the user{selectedUsers.size > 1 ? "s" : ""} from the
            database.
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={TrashIcon}
              onClick={() => {
                axiosInstance
                  .post(
                    `${window.REACT_APP_API_ENDPOINT}/trash/delete-permanently`,
                    {
                      ids: Array.from(selectedUsers),
                      type: "users",
                    }
                  )
                  .then(() => {
                    queryClient.invalidateQueries(["users"]);
                    setSelectedUsers(new Set());
                    setSelectAll(false);
                    closeDialog();
                    toast.success("Users deleted permanently!");
                  })
                  .catch(() => {
                    toast.error(
                      "Failed to delete some users permanently. Please try again."
                    );
                  });
              }}
            >
              Delete Permanently
            </Button>
          </div>
        </div>
      ),
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
            <Button
              variant="outline-info"
              icon={<ArrowLeftIcon className="h-4 w-4" />}
              onClick={() => navigate(`/admin/trash`)}
              className="p-1"
            >
              Back to Trash Categories
            </Button>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <div className="flex flex-col gap-2">
                  <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                    Deleted Users
                  </h4>
                  {selectedUsers.size > 0 && (
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>
                        {selectedUsers.size} user
                        {selectedUsers.size > 1 ? "s" : ""} selected
                      </span>
                      <Button
                        variant="light"
                        size="xs"
                        onClick={handleClearSelection}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 lg:flex-row items-center">
                  {selectedUsers.size > 0 && (
                    <>
                      <Button
                        variant="outline-success"
                        size="sm"
                        icon={ArrowPathIcon}
                        onClick={handleBulkRestore}
                      >
                        Restore Selected ({selectedUsers.size})
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        icon={TrashIcon}
                        onClick={handleBulkDelete}
                        className=""
                      >
                        Delete Permanently ({selectedUsers.size})
                      </Button>
                    </>
                  )}
                  <button
                    onClick={handleClearFilters}
                    className="bg-gray-200 text-gray-800 rounded-md px-3 py-2 text-sm font-bold mb-2 lg:mb-0 flex items-center gap-2"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Clear Filters
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="First Name"
                            columnKey="name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by First Name..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Last Name"
                            columnKey="last_name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Last Name..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="User ID"
                            columnKey="user_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by User ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Username"
                            columnKey="username"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Username..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Status"
                            columnKey="status"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Status..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Role"
                            columnKey="role"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Role..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : usersList.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <NoData message="No users found" />
                        </td>
                      </tr>
                    ) : (
                      usersList.map((user, index) => (
                        <tr
                          key={user.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.id)}
                              onChange={() => handleSelectUser(user.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={user.name}
                                displayValue={user.name}
                                id={user.id}
                                field="name"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    userId: data.id,
                                    field: "name",
                                    value: data.value.split(" ")[0],
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={user.last_name}
                                displayValue={user.last_name}
                                id={user.id}
                                field="last_name"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    userId: data.id,
                                    field: "last_name",
                                    value: data.value.split(" ")[0],
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={user.user_id}
                              displayValue={user.user_id}
                              id={user.id}
                              field="user_id"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  userId: data.id,
                                  field: "user_id",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={user.username}
                              displayValue={user.username}
                              id={user.id}
                              maxLength={20}
                              field="username"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  userId: data.id,
                                  field: "username",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              {user.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableSelectCell
                              value={user.role}
                              displayValue={user.role}
                              id={user.id}
                              options={roleOptions}
                              field="role"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  userId: data.id,
                                  field: "role",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4 gap-2">
                              <Button
                                variant="outline-success"
                                size="icon"
                                icon={<ArrowPathIcon className="h-4 w-4" />}
                                onClick={() =>
                                  handleRestore(
                                    user.id,
                                    `${user.name} ${user.last_name}`
                                  )
                                }
                                className="p-1"
                              />
                              <Button
                                variant="outline-danger"
                                size="icon"
                                icon={<TrashIcon className="h-4 w-4" />}
                                onClick={() =>
                                  handleDelete(
                                    user.id,
                                    `${user.name} ${user.last_name}`
                                  )
                                }
                                className="p-1"
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={users?.data?.current_page}
                last_page={users?.data?.last_page}
                next_page_url={users?.data?.next_page_url}
                prev_page_url={users?.data?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
